import { ReactElement, useCallback } from 'react';
import { DialogTitle } from '@mui/material';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';

import { generateRoute } from '~services/route';
import { useUtcOffset, useLazyRequest } from '~hooks/index';

import {
  getDefaultScheduleStartDate,
  getPointDataByWarehouse,
} from '../../utils/helpers';
import { SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME } from '../../utils/scheduleRouteForm';

import { useAssistantGenerateRoutesDialogByItemsContext } from '../Context';
import {
  FORM_BY_ITEMS_FIELDS_NAME,
  getFormByItemsInitialValues,
  getValidationFormByItemsSchema,
  isSubmiTypeGenerateTemplate,
  getGenerateTemplateRouteBody,
  getGenerateRouteWithScheduleBody,
  getGenerateRouteBody,
} from '../utils';

import AssistantGenerateRoutesDialogByItemsContentTabs from './Tabs';
import AssistantGenerateRoutesDialogByItemsContentActions from './Actions';

const AssistantGenerateRoutesDialogByItemsContent = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const formatterDateUtcOffset = useUtcOffset();

  const {
    activeTab: { setActiveTab },
    data: {
      warehouses: { lastSelectedWarehouse },
      selectedDriver: { value: selectedDriver },
      isFeedbackRequestAudit,
    },
    form: {
      selection: {
        orders: { selectedRowsIds: selectedItemsIds },
        vehicles: { selectedRowsIds: selectedVehiclesIds },
      },
    },
    stepper: {
      currentApiTransactionId: { setCurrentApiTransactionId },
    },
    actions,
  } = useAssistantGenerateRoutesDialogByItemsContext();

  const [, , , executeGenerateRoute] = useLazyRequest({
    request: generateRoute,
    withPostSuccess: (response) => {
      const apiTransactionId = response.data?.data.apiTransactionId as string;

      actions?.onGenerateRouteComplete?.(apiTransactionId);

      setCurrentApiTransactionId(apiTransactionId);

      setActiveTab('stepper');
    },
    withPostFailure: (err) => {
      let errorMessage = 'Ha ocurrido un error, intente nuevamente';

      if (err?.data?.data.message) {
        errorMessage = err.data.data.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const handleGenerateTemplateRoute = useCallback(
    async (formValues: ReturnType<typeof getFormByItemsInitialValues>) => {
      const body = getGenerateTemplateRouteBody({
        itemsId: selectedItemsIds,
        vehicleId: selectedVehiclesIds[0],
        formValues,
        formatterDateUtcOffset,
      });

      await executeGenerateRoute(body);
    },
    [
      selectedItemsIds,
      selectedVehiclesIds,
      formatterDateUtcOffset,
      executeGenerateRoute,
    ],
  );

  const handleGenerateRouteWithSchedule = useCallback(
    async (formValues: ReturnType<typeof getFormByItemsInitialValues>) => {
      const body = getGenerateRouteWithScheduleBody({
        itemsId: selectedItemsIds,
        vehicleId: selectedVehiclesIds[0],
        selectedDriver,
        formValues,
        formatterDateUtcOffset,
      });

      await executeGenerateRoute(body);
    },
    [
      selectedItemsIds,
      selectedVehiclesIds,
      selectedDriver,
      formatterDateUtcOffset,
      executeGenerateRoute,
    ],
  );

  const handleGenerateSimpleRoute = useCallback(
    async (formValues: ReturnType<typeof getFormByItemsInitialValues>) => {
      const body = getGenerateRouteBody({
        itemsId: selectedItemsIds,
        vehiclesId: selectedVehiclesIds,
        formValues,
        formatterDateUtcOffset,
      });

      await executeGenerateRoute(body);
    },
    [
      selectedItemsIds,
      selectedVehiclesIds,
      formatterDateUtcOffset,
      executeGenerateRoute,
    ],
  );

  const handleSubmit = useCallback(
    async (
      formValues: ReturnType<typeof getFormByItemsInitialValues>,
      {
        setSubmitting,
      }: FormikHelpers<ReturnType<typeof getFormByItemsInitialValues>>,
    ) => {
      const submitType = formValues[FORM_BY_ITEMS_FIELDS_NAME.SUBMIT_TYPE];

      if (isSubmiTypeGenerateTemplate(submitType)) {
        await handleGenerateTemplateRoute(formValues);

        return;
      }

      const isScheduleRouteEnabled =
        formValues[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED];

      if (isScheduleRouteEnabled) {
        await handleGenerateRouteWithSchedule(formValues);

        return;
      }

      await handleGenerateSimpleRoute(formValues);

      setSubmitting(false);
    },
    [
      handleGenerateRouteWithSchedule,
      handleGenerateSimpleRoute,
      handleGenerateTemplateRoute,
    ],
  );

  const formikBag = useFormik({
    initialValues: getFormByItemsInitialValues({
      defaultScheduleDate: getDefaultScheduleStartDate(),
      defaultStartingPoint: getPointDataByWarehouse(lastSelectedWarehouse),
    }),
    validationSchema: getValidationFormByItemsSchema(isFeedbackRequestAudit),
    onSubmit: handleSubmit,
  });

  return (
    <>
      <FormikProvider value={formikBag}>
        <DialogTitle>Generar Rutas</DialogTitle>

        <AssistantGenerateRoutesDialogByItemsContentTabs />

        <AssistantGenerateRoutesDialogByItemsContentActions />
      </FormikProvider>
    </>
  );
};

export default AssistantGenerateRoutesDialogByItemsContent;
